import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sparkles, Layers, ArrowRight, ChevronLeft, ChevronRight, Zap } from "lucide-react";
import { api, SUBJECTS, CLASSES } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

export default function FlashcardsPage() {
  const { user } = useAuth();
  const [cls, setCls] = useState(user?.student_class || "10");
  const [subject, setSubject] = useState("Mathematics");
  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [reviewedXp, setReviewedXp] = useState(0);

  useEffect(() => {
    const params = { class_level: cls };
    if (subject) params.subject = subject;
    api.get("/flashcards", { params }).then((r) => {
      setCards(r.data);
      setIdx(0);
      setFlipped(false);
    }).catch(() => setCards([]));
  }, [cls, subject]);

  const total = cards.length;
  const card = useMemo(() => cards[idx], [cards, idx]);

  const advance = () => {
    if (idx + 1 < total) {
      setIdx(idx + 1);
      setFlipped(false);
    } else {
      toast.success("You've reviewed the whole deck ✨");
    }
  };

  const grade = async (g) => {
    if (!user) return toast.error("Sign in to review flashcards");
    try {
      const { data } = await api.post("/flashcards/review", { card_id: card.card_id, grade: g });
      setReviewedXp((x) => x + (data.xp_earned || 0));
      advance();
    } catch {
      toast.error("Could not save review");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 pt-32 pb-16">
        <div className="text-xs uppercase tracking-widest font-semibold text-secondary mb-3">Flashcards</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">
          Recall. Reinforce. Remember.
        </h1>
        <p className="text-muted-foreground mt-3 max-w-xl">
          Flip a card, rate how well you knew it, and keep building your memory palace.
        </p>

        <div className="mt-10 flex items-center gap-3 flex-wrap">
          <Select value={cls} onValueChange={setCls}>
            <SelectTrigger className="h-11 rounded-xl w-40" data-testid="flash-class-filter">
              <SelectValue placeholder="Class" />
            </SelectTrigger>
            <SelectContent>
              {CLASSES.map((c) => (
                <SelectItem key={c} value={c}>
                  Class {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={subject} onValueChange={setSubject}>
            <SelectTrigger className="h-11 rounded-xl w-52" data-testid="flash-subject-filter">
              <SelectValue placeholder="Subject" />
            </SelectTrigger>
            <SelectContent>
              {SUBJECTS.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="ml-auto text-sm text-muted-foreground inline-flex items-center gap-1.5">
            <Zap className="w-4 h-4 text-orange-500" /> +{reviewedXp} XP this session
          </div>
        </div>

        {total === 0 ? (
          <div className="mt-10 glass rounded-3xl p-16 text-center">
            <Layers className="w-12 h-12 text-primary mx-auto mb-4 opacity-60" />
            <div className="font-display text-2xl font-bold">No flashcards for this filter yet</div>
            <p className="text-muted-foreground mt-2">Try changing the class or subject.</p>
          </div>
        ) : (
          <div className="mt-10">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
              <span>
                Card <b className="text-foreground">{idx + 1}</b> of {total} · {card.chapter || "Mixed"}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => {
                    if (idx > 0) {
                      setIdx(idx - 1);
                      setFlipped(false);
                    }
                  }}
                  disabled={idx === 0}
                  data-testid="flash-prev-btn"
                  className="w-8 h-8 rounded-full hover:bg-muted disabled:opacity-30 flex items-center justify-center"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    if (idx + 1 < total) {
                      setIdx(idx + 1);
                      setFlipped(false);
                    }
                  }}
                  disabled={idx + 1 >= total}
                  data-testid="flash-next-btn"
                  className="w-8 h-8 rounded-full hover:bg-muted disabled:opacity-30 flex items-center justify-center"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div
              className="relative rounded-3xl overflow-hidden cursor-pointer"
              onClick={() => setFlipped((f) => !f)}
              data-testid="flashcard"
              style={{ perspective: 1200 }}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={flipped ? "back" : "front"}
                  initial={{ rotateY: 90, opacity: 0 }}
                  animate={{ rotateY: 0, opacity: 1 }}
                  exit={{ rotateY: -90, opacity: 0 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  className={`min-h-[300px] md:min-h-[360px] rounded-3xl p-10 flex flex-col items-center justify-center text-center ${
                    flipped
                      ? "bg-gradient-to-br from-indigo-500/15 via-cyan-500/15 to-purple-500/15 border border-primary/30"
                      : "glass"
                  }`}
                >
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-background/40 mb-5">
                    <Sparkles className="w-3 h-3 text-secondary" />
                    <span className="text-[10px] uppercase tracking-widest font-semibold">
                      {flipped ? "Answer" : "Question"}
                    </span>
                  </div>
                  <div className="font-display text-2xl md:text-3xl font-bold tracking-tight max-w-2xl">
                    {flipped ? card.back : card.front}
                  </div>
                  <div className="mt-6 text-xs text-muted-foreground">Tap card to {flipped ? "hide" : "reveal"}</div>
                </motion.div>
              </AnimatePresence>
            </div>

            {flipped && (
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { g: "again", label: "Again", color: "border-rose-500/30 text-rose-500" },
                  { g: "hard", label: "Hard", color: "border-amber-500/30 text-amber-500" },
                  { g: "good", label: "Good", color: "border-emerald-500/30 text-emerald-500" },
                  { g: "easy", label: "Easy", color: "border-cyan-500/30 text-cyan-500" },
                ].map((b) => (
                  <button
                    key={b.g}
                    onClick={() => grade(b.g)}
                    data-testid={`flash-grade-${b.g}`}
                    className={`glass rounded-2xl p-4 border-2 ${b.color} hover:scale-[1.02] transition-transform font-semibold`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            )}

            {!flipped && (
              <div className="mt-6 flex justify-center">
                <Button
                  onClick={() => setFlipped(true)}
                  data-testid="flash-reveal-btn"
                  className="rounded-full h-11 px-6 bg-primary hover:bg-primary/90"
                >
                  Reveal answer <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
